import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Home, Users, KeyRound, Sparkles, LogOut } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { createFamily, joinFamily } from '../../services/familyService';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Modal from '../../components/ui/Modal';
import { ROUTES } from '../../config/constants';

const OnboardingPage = () => {
  const { profile, refreshProfile, signOut, hasFamily } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();

  // Escape hatch automático: si el usuario YA tiene hogar (caso raro pero
  // posible: timing de carga, navegación manual a /onboarding, etc.),
  // redirigirlo a /pets en vez de mostrar el form de "crear hogar nuevo"
  // (lo que provoca el atrapamiento histórico donde no podía salir).
  useEffect(() => {
    if (hasFamily) {
      navigate(ROUTES.PETS, { replace: true });
    }
  }, [hasFamily, navigate]);

  const handleSignOutEscape = async () => {
    try {
      await signOut();
      navigate(ROUTES.LOGIN, { replace: true });
    } catch (err) {
      toast.error(err.message || 'No se pudo cerrar sesión.');
    }
  };

  const [activeFlow, setActiveFlow] = useState(null); // 'create' | 'join' | null
  const [submitting, setSubmitting] = useState(false);
  const [createdInfo, setCreatedInfo] = useState(null); // { familyId, code }

  // Forms
  const [familyName, setFamilyName] = useState('');
  const [inviteCode, setInviteCode] = useState('');
  const [formError, setFormError] = useState(null);

  const closeFlow = () => {
    if (submitting) return;
    setActiveFlow(null);
    setFamilyName('');
    setInviteCode('');
    setFormError(null);
  };

  const handleCreate = async () => {
    setFormError(null);
    if (!familyName.trim()) {
      setFormError('Ingresá un nombre para tu hogar.');
      return;
    }

    try {
      setSubmitting(true);
      const result = await createFamily({ uid: profile.uid, name: familyName });
      setCreatedInfo(result);
      // No refrescamos el perfil aún para mantener el modal de éxito visible.
      // El refresh ocurre cuando el usuario tappea "Continuar".
    } catch (err) {
      setFormError(err.message || 'No se pudo crear el hogar.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleJoin = async () => {
    setFormError(null);
    if (!inviteCode.trim()) {
      setFormError('Ingresá el código de invitación.');
      return;
    }

    try {
      setSubmitting(true);
      await joinFamily({ uid: profile.uid, code: inviteCode });
      await refreshProfile();
      toast.success('¡Te uniste al hogar!');
      navigate(ROUTES.PETS, { replace: true });
    } catch (err) {
      setFormError(err.message || 'No se pudo unir al hogar.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleContinueAfterCreate = async () => {
    await refreshProfile();
    toast.success('Hogar creado correctamente');
    navigate(ROUTES.PETS, { replace: true });
  };

  return (
    <>
      <div className="flex min-h-[100dvh] flex-col bg-slate-50 px-4 py-8 pt-safe">
        <div className="mx-auto w-full max-w-md">
          <div className="mb-6 text-center">
            <h1 className="text-2xl font-bold text-slate-900">
              Hola {profile?.displayName?.split(' ')[0]} 👋
            </h1>
            <p className="mt-2 text-sm text-slate-600">
              Para empezar, creá un hogar o unite a uno existente.
            </p>
          </div>

          <div className="flex flex-col gap-3">
            <Card>
              <div className="flex items-start gap-3">
                <div className="rounded-lg bg-brand-100 p-2.5">
                  <Home className="h-5 w-5 text-brand-700" />
                </div>
                <div className="flex-1">
                  <h2 className="font-semibold text-slate-900">Crear un hogar</h2>
                  <p className="mt-1 text-sm text-slate-600">
                    Sos quien gestiona las mascotas. Vas a poder invitar a otros miembros.
                  </p>
                  <Button
                    variant="primary"
                    size="sm"
                    className="mt-3"
                    onClick={() => setActiveFlow('create')}
                  >
                    Crear hogar
                  </Button>
                </div>
              </div>
            </Card>

            <Card>
              <div className="flex items-start gap-3">
                <div className="rounded-lg bg-slate-100 p-2.5">
                  <Users className="h-5 w-5 text-slate-700" />
                </div>
                <div className="flex-1">
                  <h2 className="font-semibold text-slate-900">Unirme a un hogar</h2>
                  <p className="mt-1 text-sm text-slate-600">
                    Ingresá el código de invitación que te compartió un familiar.
                  </p>
                  <Button
                    variant="secondary"
                    size="sm"
                    className="mt-3"
                    onClick={() => setActiveFlow('join')}
                  >
                    Ingresar código
                  </Button>
                </div>
              </div>
            </Card>
          </div>

          {/* Escape hatch defensivo: si el usuario terminó acá por error
              (timing de carga, navegación rara) puede cerrar sesión y volver
              a empezar. Sin este botón, en versiones previas quedaba
              atrapado y solo podía salir borrando localStorage. */}
          <div className="mt-8 text-center">
            <button
              type="button"
              onClick={handleSignOutEscape}
              className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-700 transition-colors"
            >
              <LogOut className="h-3.5 w-3.5" aria-hidden="true" />
              Cerrar sesión
            </button>
          </div>
        </div>
      </div>

      {/* Modal: Crear hogar */}
      <Modal
        isOpen={activeFlow === 'create' && !createdInfo}
        onClose={closeFlow}
        title="Crear hogar"
      >
        <div className="flex flex-col gap-4">
          <Input
            label="Nombre del hogar"
            placeholder="Ej: Casa de los López"
            value={familyName}
            onChange={(e) => setFamilyName(e.target.value)}
            error={formError}
            maxLength={40}
            autoFocus
            onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
          />
          <div className="flex gap-2">
            <Button variant="ghost" fullWidth onClick={closeFlow} disabled={submitting}>
              Cancelar
            </Button>
            <Button
              variant="primary"
              fullWidth
              isLoading={submitting}
              onClick={handleCreate}
            >
              Crear
            </Button>
          </div>
        </div>
      </Modal>

      {/* Modal: Hogar creado con éxito */}
      <Modal
        isOpen={Boolean(createdInfo)}
        onClose={handleContinueAfterCreate}
        title="¡Hogar creado!"
      >
        <div className="flex flex-col items-center gap-4 text-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-brand-100">
            <Sparkles className="h-6 w-6 text-brand-700" />
          </div>
          <p className="text-sm text-slate-600">
            Compartí este código con quienes quieras invitar:
          </p>
          <div className="w-full rounded-xl border-2 border-dashed border-brand-300 bg-brand-50 px-6 py-4">
            <p className="font-mono text-3xl font-bold tracking-[0.3em] text-brand-800">
              {createdInfo?.code}
            </p>
          </div>
          <p className="text-xs text-slate-500">
            Vas a poder verlo siempre desde la sección de configuración del hogar.
          </p>
          <Button variant="primary" fullWidth onClick={handleContinueAfterCreate}>
            Continuar
          </Button>
        </div>
      </Modal>

      {/* Modal: Unirse con código */}
      <Modal
        isOpen={activeFlow === 'join'}
        onClose={closeFlow}
        title="Unirme a un hogar"
      >
        <div className="flex flex-col gap-4">
          <Input
            label="Código de invitación"
            placeholder="ABC123"
            value={inviteCode}
            onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
            error={formError}
            leftIcon={KeyRound}
            maxLength={6}
            autoCapitalize="characters"
            autoComplete="off"
            spellCheck={false}
            autoFocus
            className="font-mono tracking-widest"
            onKeyDown={(e) => e.key === 'Enter' && handleJoin()}
          />
          <div className="flex gap-2">
            <Button variant="ghost" fullWidth onClick={closeFlow} disabled={submitting}>
              Cancelar
            </Button>
            <Button
              variant="primary"
              fullWidth
              isLoading={submitting}
              onClick={handleJoin}
            >
              Unirme
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default OnboardingPage;